import type { ListFilters } from '@/hooks/useAppointments';
import {
  CLINIC_TIMEZONE,
  endOfZonedDay,
  endOfZonedWeek,
  startOfZonedDay,
  startOfZonedWeek,
  zonedYmd,
} from '@/lib/reporting/timezone';
import { appointmentDestination, destinationHref } from '@/utils/contextualDestinations';

export type DashboardAppointmentSpan = 'today' | 'week';

/**
 * Shared Forside appointment scope (CLISUP-85).
 *
 * "Aftaler i dag", "Dagens program" and "Aftaler denne uge" use the same
 * query: the active clinic / sub-clinic, Europe/Copenhagen civil day or
 * week, the signed-in user as clinician, and `useAppointmentsInRange`
 * statuses (cancelled excluded).
 *
 * Admins and reception without their own appointments keep this same
 * personal filter. Dagens program already listed only `user.id`, so a
 * clinic-wide KPI would disagree with the list. Clinic-wide numbers stay
 * on Aktive patienter, Udestående fakturaer and SMS-saldo.
 */
export function dashboardAppointmentDateParam(now: Date): string {
  return zonedYmd(now, CLINIC_TIMEZONE);
}

export function dashboardAppointmentRange(
  now: Date,
  span: DashboardAppointmentSpan,
): Pick<ListFilters, 'start' | 'end'> {
  switch (span) {
    case 'today':
      return {
        start: startOfZonedDay(now, CLINIC_TIMEZONE).toISOString(),
        end: endOfZonedDay(now, CLINIC_TIMEZONE).toISOString(),
      };
    case 'week':
      return {
        start: startOfZonedWeek(now, CLINIC_TIMEZONE).toISOString(),
        end: endOfZonedWeek(now, CLINIC_TIMEZONE).toISOString(),
      };
    default: {
      const _exhaustive: never = span;
      return _exhaustive;
    }
  }
}

export function dashboardAppointmentFilters(
  now: Date,
  clinicianId: string | null,
  span: DashboardAppointmentSpan = 'today',
): ListFilters {
  return {
    ...dashboardAppointmentRange(now, span),
    clinicianId,
  };
}

/** Do not fetch clinic-wide while the signed-in user is unknown. */
export function dashboardAppointmentClinicId(
  clinicId: string | null,
  userId: string | null | undefined,
): string | null {
  return userId ? clinicId : null;
}

export function dashboardAppointmentCalendarHref(
  now: Date,
  clinicianId: string | null,
  span: DashboardAppointmentSpan = 'today',
): string {
  const date = dashboardAppointmentDateParam(now);
  let filters: { view: 'day' | 'week'; date: string };
  switch (span) {
    case 'today':
      filters = { view: 'day', date };
      break;
    case 'week':
      filters = { view: 'week', date };
      break;
    default: {
      const _exhaustive: never = span;
      return _exhaustive;
    }
  }
  const href = destinationHref(appointmentDestination(filters));
  if (!clinicianId) return href;
  const [path, query = ''] = href.split('?');
  const params = new URLSearchParams(query);
  params.set('staff', clinicianId);
  return `${path}?${params.toString()}`;
}
